import { api, LightningElement, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
// eslint-disable-next-line local/import-labels-adapter
import { I18n } from "@devforce/trailhead-client-locale";
import classNames from "classnames";

import * as locales from "./locales";

import { attrToBool, htmlToText } from "common/helpers";
import { formatShortDate } from "../../common/helpers/time";
import { assignedSlotNames } from "common/slot";
import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";
import { createTrack, TrackEvent } from "common/track";
import { TruncateType, TruncateDisplayMode } from "tds/truncate";
import { SummaryDetail } from "../contentSummary/types";
import { Config } from "tds/sanitizedHtml";

type TitleMode = "anchor" | "button" | "text" | "html";

export type Size = "default" | "small";
export type Variant =
  | "brick"
  | "brick-tile"
  | "tile"
  | "tile-brick"
  | "tile-details";

const i18n = new I18n({ summary: locales });

export type ResizeEvent = { entry: ResizeObserverEntry; size: Size };
export default class extends LightningElement {
  @api description: string | null = null;
  @api descriptionTruncateType: TruncateType | null = null;
  @api descriptionTruncateDisplayMode: TruncateDisplayMode | null = null;
  @api descriptionTruncateLabel: string | null = null;
  @api descriptionHtml: string | null = null;
  @api details?: SummaryDetail[] | null = null;
  @api path: string | null = null;
  @api pathTarget: string | null = null;
  @api hideNewWindowIcon?: boolean;
  @api pretitle: string | null = null;
  @api pretitleLinkHref: string | null = null;
  @api pretitleLinkTarget: string | null = null;
  @api pretitleLinkTitle: string | null = null;
  @api pretitleTruncateLines: string | number = 1;
  @api pretitleHtml: string | null = null;
  @api title: string = "";
  @api htmlConfig: Config | null = null;
  @api titleHeadingLevel: "1" | "2" | "3" | "4" | null = null;
  @api titleTruncateLines: string | number = 2;
  @api type: string = "";
  @api descriptionTruncateLines: string | number = 2;
  @api variant: Variant = "brick";
  @api metaLeftTruncateLines: string | number = 1;
  @api metaRightTruncateLines: string | number = 1;
  @api dynamicCta?: boolean;
  @api dynamicCtaIcon?: boolean;
  @api dynamicCtaIconSymbol?: string | null = null;
  @api updatedAt: string | null = null;

  @api
  get titleMode(): TitleMode {
    // Backwards compatability
    if (this._titleMode === "anchor" && !this.path) {
      if (process.env.NODE_ENV === "development") {
        logger.warn(
          `title-mode was set to "anchor" but is missing "path" attribute, falling back to "button"`
        );
      }
      return "button";
    }
    return this._titleMode;
  }
  set titleMode(value) {
    this._titleMode = value;
  }
  private _titleMode: TitleMode = "anchor";

  private size: Size = "default";
  private slots = new Set<string>();
  private slotNames = "";
  private width = Number.MAX_SAFE_INTEGER;

  private track = createTrack<"tds-summary">(this);

  private get contentAlignment() {
    return this.isCompact ? "center" : "start";
  }

  private get computedVariant() {
    switch (this.variant) {
      case "brick-tile":
        return this.width < 350 ? "tile" : "brick";
      case "tile-brick":
        return this.width > 500 ? "brick" : "tile";
      default:
        return this.variant;
    }
  }

  private get isCompact() {
    return (
      !this.description &&
      !this.descriptionHtml &&
      !this.slots.has("meta-left") &&
      !this.slots.has("meta-right")
    );
  }

  private get isTile() {
    return this.computedVariant === "tile";
  }

  private get assistivePretitle() {
    if (this.pretitleHtml) {
      // check if a pretitle plain text is provided alongside pretitleHTML, if so use it as assistive text, if not generate.
      return this.pretitle || htmlToText(this.pretitleHtml);
    }

    return this.pretitle;
  }

  private get showPretitle() {
    return (
      typeof this.pretitle === "string" ||
      typeof this.pretitleLinkTitle === "string" ||
      typeof this.pretitleHtml === "string" ||
      this.slots.has("pretitle")
    );
  }

  private get titleIsAnchor() {
    return this.titleMode === "anchor";
  }

  private get titleIsButton() {
    return this.titleMode === "button";
  }

  private get titleIsText() {
    return this.titleMode === "text";
  }

  private get titleIsHTML() {
    return this.titleMode === "html";
  }

  private get titleTruncateLineSize() {
    return 18 * 1.3; // font-size * line-height
  }

  private get metaTruncateLineSize() {
    return 14 * 1.4; // font-size * line-height
  }

  private get showDescription() {
    return this.description || this.descriptionHtml;
  }

  private get showDetails() {
    return this.details?.length;
  }

  private get showUpdatedAt() {
    return this.updatedAt;
  }

  private get descriptionClassName() {
    return classNames("content__description", {
      "content__description--html": !!this.descriptionHtml,
    });
  }

  private get displayDynamicCta() {
    return attrToBool(this.dynamicCta);
  }

  private get displayDynamicCtaIcon() {
    return attrToBool(this.dynamicCtaIcon);
  }

  private get displayNewWindowIcon() {
    return !attrToBool(this.hideNewWindowIcon) && this.pathTarget === "_blank";
  }

  private get dynamicCtaText() {
    switch (this.type) {
      case "module":
      case "project":
      case "trail":
      case "superbadge":
      case "trailmix":
      case "learningpath":
        return this.labels.learningDynamicCta;
      case "group":
        return this.labels.groupDynamicCta;
      case "topic":
        return this.labels.topicDynamicCta;
      case "job":
        return this.labels.jobDynamicCta;
      default:
        throw new Error(`Unsupported type for dynamic cta: "${this.type}"`);
    }
  }

  private get dynamicCtaAriaLabel() {
    return `${this.dynamicCtaText}: ${this.title}`;
  }

  private get updatedAtLabel() {
    return i18n.t("summary", "lastUpdated", {
      date: formatShortDate(this.updatedAt || ""),
    });
  }

  @wire(ResizeObserverAdapter, { template: "$template" })
  private onResize(entry: ResizeObserverEntry) {
    const breakpoint = this.variant === "tile" ? 280 : 400;
    this.size = entry.contentRect.width < breakpoint ? "small" : "default";
    this.width = entry.contentRect.width;
    this.dispatchEvent(
      new CustomEvent("resize", { detail: { entry, size: this.size } })
    );
  }

  private onSlotChange() {
    let slots = assignedSlotNames(this.template);
    this.slots = new Set(slots);
    this.slotNames = slots.join(" ");
  }

  private onTitleClick() {
    this.dispatchEvent(new CustomEvent("navigate"));
  }

  private onCtaClick() {
    this.dispatchEvent(
      new CustomEvent("dynamicctaclick", {
        detail: { ctaLabel: this.dynamicCtaText },
      })
    );
  }

  private handleDescriptionTruncateTrack(e: TrackEvent<"tds-truncate">) {
    this.track("truncate_link_clicked", e.detail.payload);
  }

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;
}
